import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Toast } from '@shopify/polaris';

interface ToastMessage {
  id: string;
  content: string;
  error?: boolean;
  duration?: number;
}

interface ToastContextType {
  showToast: (content: string, error?: boolean, duration?: number) => void;
  showSuccess: (content: string, duration?: number) => void;
  showError: (content: string, duration?: number) => void;
  hideToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = (content: string, error = false, duration = 5000) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: ToastMessage = { id, content, error, duration };
    
    setToasts(prev => [...prev, newToast]);
    
    // Auto-hide toast after duration
    setTimeout(() => {
      hideToast(id);
    }, duration);
  };

  const showSuccess = (content: string, duration = 5000) => {
    showToast(content, false, duration);
  };

  const showError = (content: string, duration = 5000) => {
    showToast(content, true, duration);
  };

  const hideToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const toastMarkup = toasts.map(toast => (
    <Toast
      key={toast.id}
      content={toast.content}
      error={toast.error}
      onDismiss={() => hideToast(toast.id)}
    />
  ));

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, hideToast }}>
      {children}
      {toastMarkup}
    </ToastContext.Provider>
  );
};
